/**
 * Chip Components
 *
 * Reusable chip components with consistent styling for status, metrics, and ratings.
 */
import React from "react";
import {
  Chip as MuiChip,
  ChipProps as MuiChipProps,
  SxProps,
  Theme,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  DirectionsCar,
  TwoWheeler,
  // AttachMoney,
  CurrencyRupee,
  ConfirmationNumber,
  TrendingUp,
  SentimentVerySatisfied,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
} from "@mui/icons-material";

// ================================
// Status Chip
// ================================

interface StatusChipProps extends Omit<MuiChipProps, "color"> {
  status: "success" | "failed" | "pending" | "completed";
}

export const StatusChip: React.FC<StatusChipProps> = ({ status, ...props }) => {
  const config = {
    success: {
      color: "success" as const,
      icon: <CheckCircle fontSize="small" />,
      label: "Success",
    },
    failed: {
      color: "error" as const,
      icon: <Cancel fontSize="small" />,
      label: "Failed",
    },
    pending: {
      color: "warning" as const,
      icon: <TrendingUp fontSize="small" />,
      label: "Pending",
    },
    completed: {
      color: "success" as const,
      icon: <CheckCircle fontSize="small" />,
      label: "Completed",
    },
  };

  const { color, icon, label } = config[status];

  return (
    <MuiChip
      label={label}
      size="small"
      color={color}
      icon={icon}
      variant="outlined"
      sx={{
        fontWeight: 600,
        textTransform: "capitalize",
      }}
      {...props}
    />
  );
};

// ================================
// Metric Chip (with icon)
// ================================

interface MetricChipProps {
  value: string | number;
  type?: "car" | "bike" | "ticket" | "revenue" | "activity";
  bgcolor?: string;
  color?: string;
  size?: "small" | "medium";
}

export const MetricChip: React.FC<MetricChipProps> = ({
  value,
  type = "ticket",
  bgcolor,
  color,
  size = "small",
}) => {
  const iconMap = {
    car: <DirectionsCar fontSize="small" />,
    bike: <TwoWheeler fontSize="small" />,
    ticket: <ConfirmationNumber fontSize="small" />,
    revenue: <CurrencyRupee fontSize="small" />,
    activity: <TrendingUp fontSize="small" />,
  };

  const colorMap = {
    car: { bgcolor: "#E3F2FD", color: "#1976D2" },
    bike: { bgcolor: "#E1F5FE", color: "#0288D1" },
    ticket: { bgcolor: "primary.50", color: "primary.main" },
    revenue: { bgcolor: "#E8F5E9", color: "#2E7D32" },
    activity: { bgcolor: "#F3E5F5", color: "#7B1FA2" },
  };

  const { bgcolor: defaultBgColor, color: defaultColor } = colorMap[type];

  return (
    <MuiChip
      icon={iconMap[type]}
      label={value}
      size={size}
      sx={{
        bgcolor: bgcolor || defaultBgColor,
        color: color || defaultColor,
        fontWeight: 600,
        borderRadius: 2,
      }}
    />
  );
};

// ================================
// Rating Chip (with sentiment icon)
// ================================

interface RatingChipProps extends Omit<MuiChipProps, "icon"> {
  rating: "excellent" | "good" | "average" | "poor";
  label?: string;
}

export const RatingChip: React.FC<RatingChipProps> = ({
  rating,
  label,
  ...props
}) => {
  const ratingConfig = {
    excellent: {
      icon: <SentimentVerySatisfied />,
      gradient: "linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)",
      label: label || "Excellent (5/5)",
    },
    good: {
      icon: <SentimentSatisfied />,
      gradient: "linear-gradient(45deg, #2196f3 30%, #42a5f5 90%)",
      label: label || "Good (4/5)",
    },
    average: {
      icon: <SentimentNeutral />,
      gradient: "linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)",
      label: label || "Average (3/5)",
    },
    poor: {
      icon: <SentimentDissatisfied />,
      gradient: "linear-gradient(45deg, #f44336 30%, #ef5350 90%)",
      label: label || "Poor (1-2/5)",
    },
  };

  const { icon, gradient, label: defaultLabel } = ratingConfig[rating];

  return (
    <MuiChip
      icon={icon}
      label={label || defaultLabel}
      size="small"
      variant="filled"
      sx={{
        fontWeight: 600,
        background: gradient,
        color: "white",
        "& .MuiChip-icon": {
          color: "white !important",
        },
      }}
      {...props}
    />
  );
};

// ================================
// Simple Chip (basic with custom colors)
// ================================

interface SimpleChipProps {
  label: string;
  bgcolor?: string;
  color?: string;
  size?: "small" | "medium";
  variant?: "filled" | "outlined";
  sx?: SxProps<Theme>;
}

export const SimpleChip: React.FC<SimpleChipProps> = ({
  bgcolor,
  color,
  size = "small",
  variant = "filled",
  sx,
  ...props
}) => (
  <MuiChip
    size={size}
    variant={variant}
    sx={{
      fontWeight: 600,
      borderRadius: 2,
      bgcolor,
      color,
      ...sx,
    }}
    {...props}
  />
);

// ================================
// Stat Chip (for hero sections with transparent background)
// ================================

interface StatChipProps {
  icon: React.ReactElement;
  label: string;
  bgcolor?: string;
  color?: string;
  size?: "small" | "medium";
}

export const StatChip: React.FC<StatChipProps> = ({
  icon,
  label,
  bgcolor = "rgba(255,255,255,0.2)",
  color = "white",
  size = "medium",
}) => {
  return (
    <MuiChip
      icon={icon}
      label={label}
      size={size}
      sx={{
        bgcolor,
        color,
        fontWeight: 600,
        fontSize: "1rem",
        px: 1,
        "& .MuiChip-icon": { color },
      }}
    />
  );
};
